import type { ImperativePanelHandle } from "react-resizable-panels";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { Awaitable } from "@alaffia-technology-solutions/types";

import { useResizeObserver } from "@alaffia-technology-solutions/hooks";

const EXPANDED_FILES_SIDEBAR_THRESHOLD = 688;
const COLLAPSED_SIZE_IN_PIXELS = 48;

interface UseResizablePanelOptions {
  /**
   * The ref of the wrapper element.
   */
  wrapperRef: React.RefObject<Element | null>;
  /**
   * The threshold at which the files sidebar should be expanded.
   */
  expandedFilesSidebarThreshold?: number;
  /**
   * The size of a collapsed panel in pixels.
   */
  collapsedSizeInPixels?: number;
  /**
   * The selector for resize handle nodes of this panel.
   */
  resizeHandleSelector?: string;
  /**
   * Indicates whether the panel should be expanded by default.
   */
  defaultExpanded?: boolean;
  /**
   * External isExpanded state.
   */
  isExpanded?: boolean;
  /**
   * External onIsExpandedChange function.
   * */
  onIsExpandedChange?: (isExpanded: boolean) => Awaitable<void>;
}

interface GetResizablePanelProps {
  /**
   * Specifies the default size of the panel.
   */
  defaultSize?: number;
}

/**
 * A hook that provides functionality to expand and collapse a panel.
 * @param {UseResizablePanelOptions} options - The options to configure the hook.
 * @returns An object containing the expand and collapse functions, the panel's isExpanded state, and resizable panel prop getter function.
 */
export const useResizablePanel = ({
  wrapperRef,
  expandedFilesSidebarThreshold = EXPANDED_FILES_SIDEBAR_THRESHOLD,
  collapsedSizeInPixels = COLLAPSED_SIZE_IN_PIXELS,
  resizeHandleSelector = "[data-panel-resize-handle-id]",
  defaultExpanded = false,
  isExpanded: externalIsExpanded,
  onIsExpandedChange,
}: UseResizablePanelOptions) => {
  const { width: wrapperWidth } = useResizeObserver({ ref: wrapperRef });

  const ref = useRef<ImperativePanelHandle>(null);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const collapsePanel = useCallback(() => {
    ref.current?.collapse();
  }, []);

  const expandPanel = useCallback(() => {
    ref.current?.expand();
  }, []);

  const onExpand = useCallback(async () => {
    onIsExpandedChange ? await onIsExpandedChange(true) : setIsExpanded(true);
  }, [onIsExpandedChange]);

  const onCollapse = useCallback(async () => {
    onIsExpandedChange ? await onIsExpandedChange(false) : setIsExpanded(false);
  }, [onIsExpandedChange]);

  useEffect(() => {
    if (wrapperWidth && wrapperWidth <= expandedFilesSidebarThreshold) {
      collapsePanel();
    }
  }, [wrapperWidth, expandedFilesSidebarThreshold, collapsePanel]);

  const [collapsedSize, setCollapsedSize] = useState(0);

  useLayoutEffect(() => {
    const resizeHandles = (
      wrapperRef.current ?? document
    ).querySelectorAll<HTMLDivElement>(resizeHandleSelector);

    let width = wrapperWidth;

    if (width !== 0) {
      resizeHandles.forEach((resizeHandle) => {
        width -= resizeHandle.offsetWidth;
      });

      setCollapsedSize((collapsedSizeInPixels / width) * 100);
    }
  }, [wrapperWidth, collapsedSizeInPixels, resizeHandleSelector, wrapperRef]);

  const getResizablePanelProps = useCallback(
    ({ defaultSize }: GetResizablePanelProps = {}) => {
      return {
        onExpand,
        onCollapse,
        collapsedSize,
        defaultSize:
          (externalIsExpanded ?? defaultExpanded) ? defaultSize : collapsedSize,
        ref,
        collapsible: true,
      };
    },
    [collapsedSize, defaultExpanded, externalIsExpanded, onCollapse, onExpand],
  );

  return {
    expandPanel,
    collapsePanel,
    isExpanded: externalIsExpanded ?? isExpanded,
    getResizablePanelProps,
  };
};
